import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  computed,
  effect,
  inject,
  input,
  signal,
} from "@angular/core";
import { FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import {
  PasswordRequirementsComponent,
  UiGoogleButtonComponent,
} from "@keus-template/ui";
import { provideIcons } from "@ng-icons/core";
import { lucideLoaderCircle, lucideEye, lucideEyeOff } from "@ng-icons/lucide";
import { HlmButtonDirective } from "@spartan-ng/ui-button-helm";
import {
  HlmCardContentDirective,
  HlmCardDirective,
  HlmCardFooterDirective,
} from "@spartan-ng/ui-card-helm";
import { HlmCheckboxComponent } from "@spartan-ng/ui-checkbox-helm";
import { HlmFormFieldModule } from "@spartan-ng/ui-formfield-helm";
import { HlmInputDirective } from "@spartan-ng/ui-input-helm";
import { HlmLabelDirective } from "@spartan-ng/ui-label-helm";
import { HlmSpinnerComponent } from "@spartan-ng/ui-spinner-helm";
import { NgIcon } from "@ng-icons/core";
import { HlmIconDirective } from "@spartan-ng/ui-icon-helm";
import {
  HlmTabsComponent,
  HlmTabsContentDirective
} from "@spartan-ng/ui-tabs-helm";
import {
  AUTH_TAB_LABELS,
  AuthTab,
} from "../../../../../../enums/auth-tab.enum";
import { LoginAndRegistrationForm } from "./login-and-registration-form.interface";

@Component({
  selector: "keus-login-and-registration-form",
  imports: [
    HlmTabsComponent,
    HlmTabsContentDirective,
    HlmCardContentDirective,
    HlmCardDirective,
    HlmCardFooterDirective,
    HlmFormFieldModule,
    HlmLabelDirective,
    HlmInputDirective,
    HlmButtonDirective,
    HlmSpinnerComponent,
    HlmCheckboxComponent,
    PasswordRequirementsComponent,
    ReactiveFormsModule,
    UiGoogleButtonComponent,
    NgIcon,
    HlmIconDirective,
  ],
  providers: [provideIcons({ lucideLoaderCircle, lucideEye, lucideEyeOff })],
  host: {
    class: "block w-full",
  },
  templateUrl: "./login-and-registration-form.component.html",
  styleUrl: "./login-and-registration-form.component.css",
})
export class LoginAndRegistrationFormComponent implements OnInit {
  /** AuthTab enum for template */
  public readonly AuthTab = AuthTab;

  /** Auth tab labels for template */
  public readonly AUTH_TAB_LABELS = AUTH_TAB_LABELS;

  /** The login and registration form group. */
  @Input({ required: false })
  public form!: FormGroup<LoginAndRegistrationForm>;

  /** Whether the form is loading. */
  public isLoading = input<boolean>(false);

  /** Whether the form is disabled. */
  public isDisabled = computed(() => {
    if (this.isLoading()) return true;
    
    // For registration, check specific validation rules
    if (this.activeTab() === 'registration') {
      return !this._registrationValidSignal();
    }
    
    // For login, use standard form validation
    return !this._formValidSignal();
  });

  /** Whether the form is valid as a signal. */
  private readonly _formValidSignal = signal<boolean>(false);

  /** Whether the registration form is valid as a signal. */
  private readonly _registrationValidSignal = signal<boolean>(false);

  /** The active tab to show */
  public readonly activeTab = input<"login" | "registration" | "google">("login");

  /** Show password signals for different fields */
  public readonly showLoginPassword = signal<boolean>(false);
  public readonly showRegistrationPassword = signal<boolean>(false);
  public readonly showConfirmPassword = signal<boolean>(false);

  /** The submit event. */
  @Output()
  public readonly submitEvent: EventEmitter<
    "signin" | "signup" | "google" | null
  > = new EventEmitter<"signin" | "signup" | "google" | null>();

  /** The reset password event. */
  @Output()
  public readonly resetPasswordEvent: EventEmitter<void> =
    new EventEmitter<void>();

  /** The tab change event. */
  @Output()
  public readonly tabChangeEvent: EventEmitter<AuthTab> =
    new EventEmitter<AuthTab>();

  /** The title subject. */
  public title = computed(() => {
    let computedTitle = "";
    switch (this.activeTab()) {
      case "login":
        computedTitle = "Accedi";
        break;
      case "registration":
        computedTitle = "Stai per scoprire il mondo di Talea";
        break;
      case "google":
        computedTitle = "Accesso professionista";
        break;
    }
    return computedTitle;
  });

  ngOnInit() {
    if (this.form) {
      // Set initial values
      this._formValidSignal.set(this.form.valid);
      this._registrationValidSignal.set(this._isRegistrationFormValid());

      // Subscribe to status changes
      this.form.statusChanges.subscribe(() => {
        this._formValidSignal.set(this.form.valid);
        this._registrationValidSignal.set(this._isRegistrationFormValid());
      });

      // Subscribe to value changes
      this.form.valueChanges.subscribe(() => {
        this._formValidSignal.set(this.form.valid);
        this._registrationValidSignal.set(this._isRegistrationFormValid());
      });
    }
  }

  /**
   * Constructor
   */
  constructor() {
    effect(() => {
      this._manageConfirmPasswordValidators(this.title());
    });
  }

  /**
   * Manages the confirm password validators.
   * @param title the title to manage.
   */
  private _manageConfirmPasswordValidators(title: string): void {
    const emailControl = this.form.controls.email;
    const passwordControl = this.form.controls.password;
    const confirmPasswordControl = this.form.controls.confirmPassword;
    if (!confirmPasswordControl) return;

    if (title === "Registrati") {
      emailControl.setValue("");
      passwordControl.setValue("");
      confirmPasswordControl.setValidators([
        Validators.required,
        Validators.pattern(
          /(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[@$!%*#?&^_-]).{8,}/,
        ),
      ]);
    } else {
      emailControl.setValue("");
      emailControl.setValidators([Validators.required, Validators.email]);
      passwordControl.setValidators([Validators.required]);
      confirmPasswordControl.clearValidators();
    }

    emailControl.updateValueAndValidity();
    passwordControl.updateValueAndValidity();
    confirmPasswordControl.updateValueAndValidity();
  }

  /**
   * Checks if the registration form is valid with specific rules.
   * @returns true if the form is valid for registration
   */
  private _isRegistrationFormValid(): boolean {
    if (!this.form) return false;

    const emailControl = this.form.controls.email;
    const passwordControl = this.form.controls.password;
    const confirmPasswordControl = this.form.controls.confirmPassword;
    const privacyAcceptedControl = this.form.controls.privacyAccepted;

    // Check if all required fields are filled
    if (!emailControl.value || !passwordControl.value || !confirmPasswordControl.value) {
      return false;
    }

    // Check if email is valid
    if (emailControl.invalid) {
      return false;
    }

    // Check if password meets requirements (strong password pattern)
    const passwordPattern = /(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[@$!%*#?&^_-]).{8,}/;
    if (!passwordPattern.test(passwordControl.value)) {
      return false;
    }

    // Check if passwords match
    const passwordsMatch = passwordControl.value === confirmPasswordControl.value;
    if (!passwordsMatch) {
      return false;
    }

    // Check if privacy policy is accepted (mandatory)
    if (!privacyAcceptedControl.value) {
      return false;
    }

    // All validation passed
    return true;
  }

  /**
   * Submits the form.
   * @param type the type of submission.
   */
  public submit(type: "signin" | "signup" | "google"): void {
    // Simply emit the submit event - let the parent handle the auth flow logic
    this.submitEvent.emit(type);
  }

  /**
   * Reset the password.
   */
  public onResetPasswordClick(): void {
    this.resetPasswordEvent.emit();
  }

  /**
   * Handles tab change and emits to parent.
   * @param tab the tab to change to
   */
  // public onTabChange(tab: AuthTab): void {
  //   const title = this.AUTH_TAB_LABELS[tab];
  //   this.setTitle(title);
  //   this.tabChangeEvent.emit(tab);
  // }

  /**
   * Toggle password visibility for login field
   */
  public toggleLoginPasswordVisibility(): void {
    this.showLoginPassword.set(!this.showLoginPassword());
  }

  /**
   * Toggle password visibility for registration field
   */
  public toggleRegistrationPasswordVisibility(): void {
    this.showRegistrationPassword.set(!this.showRegistrationPassword());
  }

  /**
   * Toggle password visibility for confirm password field
   */
  public toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword.set(!this.showConfirmPassword());
  }

}
